import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home, Loader2, CheckCircle2, AlertTriangle, XCircle,
  Lock, Unlock, Rocket, RefreshCw, Trees, Link2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_COLORS = {
  basement: '#94A3B8', ground: '#34D399', upper: '#38BDF8', attic: '#FBBF24',
};

const SeverityIcon = ({ severity }) => {
  if (severity === 'error') return <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />;
  if (severity === 'warn') return <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />;
  return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />;
};

/**
 * MaisonBatchLauncher — V14.86 « Générer tous les étages ».
 * Rapport de cohérence inter-niveaux (escaliers alignés, HSP, programme
 * des pièces, sous-sol/combles) puis lancement du batch complet.
 */
export const MaisonBatchLauncher = ({ projectId, buildParams, onJobLaunched }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [launching, setLaunching] = useState(false);
  // V14.87 — Étape terrain : sols/végétation appliqués à tous les étages.
  const [terrainSols, setTerrainSols] = useState('');
  const [terrainVegetation, setTerrainVegetation] = useState('');

  const loadReport = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/projects/${projectId}/maison/coherence-report`,
        { withCredentials: true },
      );
      setReport(r.data);
      if (!open) {
        const p = buildParams() || {};
        setTerrainSols(p.sols_exterieurs || '');
        setTerrainVegetation(p.vegetation_exterieure || '');
      }
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de charger le rapport de cohérence');
    } finally {
      setLoading(false);
    }
  };

  const launch = async () => {
    const ids = report?.ready_lot_ids || [];
    if (ids.length === 0) return;
    setLaunching(true);
    const toastId = toast.loading(`Lancement chaîné de ${ids.length} étage(s)… (RDC en premier)`);
    try {
      const params = { ...buildParams() };
      if (terrainSols.trim()) params.sols_exterieurs = terrainSols.trim();
      if (terrainVegetation.trim()) params.vegetation_exterieure = terrainVegetation.trim();
      const { data } = await axios.post(
        `${API}/projects/${projectId}/generation/create-batch`,
        { lot_ids: ids, params, priority: 0, maison_chained: true },
        { withCredentials: true },
      );
      const created = (data.created || []).length;
      const skipped = (data.skipped || []).length;
      toast.success(`${created}/${ids.length} génération(s) d'étage lancée(s)`, {
        id: toastId,
        description: skipped
          ? `${skipped} étage(s) ignoré(s) (plan manquant)`
          : 'RDC en premier, puis les autres étages avec son jardin en référence.',
      });
      onJobLaunched?.(data);
      setOpen(false);
    } catch (e) {
      toast.error('Échec du lancement maison', {
        id: toastId,
        description: e?.response?.data?.detail || e?.message || 'Réessaie.',
      });
    } finally {
      setLaunching(false);
    }
  };

  const readyCount = report?.ready_lot_ids?.length || 0;
  const warnCount = report?.counts?.warn || 0;
  const errorCount = report?.counts?.error || 0;

  return (
    <>
      <button
        type="button"
        onClick={loadReport}
        disabled={loading}
        className="mt-2 w-full flex items-center justify-center gap-2 h-9 rounded-md text-[12px] font-semibold transition-colors disabled:opacity-50"
        style={{
          background: 'rgba(217,160,91,0.12)',
          border: '1px solid rgba(217,160,91,0.35)',
          color: 'var(--c-gold)',
        }}
        data-testid="maison-batch-open-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Home className="w-4 h-4" />}
        Générer tous les étages
        <span className="text-[10px] font-normal opacity-70">— rapport de cohérence + batch maison</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0d1117] border-white/10 text-white"
                       data-testid="maison-batch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <Home className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
              Générer tous les étages — rapport de cohérence
            </DialogTitle>
            <DialogDescription className="text-[11.5px] text-gray-400">
              Vérification inter-niveaux avant lancement : escaliers superposés, programme des
              pièces, sous-sol / combles, calibration. Les avertissements n'empêchent pas la
              génération mais dégradent la cohérence du rendu.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2" data-testid="maison-batch-floors">
            {(report?.floors || []).map((f) => (
              <div key={f.floor_id}
                   className="rounded-lg border px-3 py-2"
                   style={{
                     borderColor: f.status === 'error' ? 'rgba(244,63,94,0.4)'
                       : f.status === 'warn' ? 'rgba(251,191,36,0.3)' : 'rgba(52,211,153,0.25)',
                     background: 'rgba(255,255,255,0.02)',
                   }}
                   data-testid={`maison-floor-row-${f.floor_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
                        style={{ background: `${KIND_COLORS[f.kind] || '#64748B'}22`, color: KIND_COLORS[f.kind] || '#94A3B8' }}>
                    {f.kind_label}
                  </span>
                  <span className="text-[12.5px] font-semibold">{f.name}</span>
                  <span className="text-[10px] text-gray-500">niv. {f.level >= 0 ? `+${f.level}` : f.level}</span>
                  {f.locked
                    ? <span className="inline-flex items-center gap-1 text-[9.5px] text-teal-300"><Lock className="w-3 h-3" /> verrouillé</span>
                    : <span className="inline-flex items-center gap-1 text-[9.5px] text-gray-500"><Unlock className="w-3 h-3" /> libre</span>}
                  <span className="ml-auto text-[10.5px] text-gray-400">
                    {f.n_walls} murs · {f.n_stairs} esc. · {f.n_openings} menuis. · HSP {Number(f.hsp_m).toFixed(2)} m
                  </span>
                </div>
                {f.rooms?.length > 0 && (
                  <div className="mt-1 text-[10px] text-gray-400">
                    <span className="text-gray-500">Programme :</span>{' '}
                    {f.rooms.join(' · ')}
                  </div>
                )}
                <ul className="mt-1.5 space-y-0.5">
                  {(f.checks || []).map((c, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[10.5px] leading-snug"
                        style={{ color: c.severity === 'error' ? '#FDA4AF' : c.severity === 'warn' ? '#FCD34D' : '#6EE7B7' }}>
                      <SeverityIcon severity={c.severity} />
                      {c.message}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* V14.87 — Étape Terrain / Jardin : le RDC est la référence */}
          <div className="rounded-lg border px-3 py-2.5"
               style={{ borderColor: 'rgba(52,211,153,0.25)', background: 'rgba(52,211,153,0.04)' }}
               data-testid="maison-terrain-panel">
            <div className="flex items-center gap-2 flex-wrap">
              <Trees className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[12px] font-semibold text-emerald-300">Terrain / Jardin — référence RDC</span>
              {report?.terrain_reference?.present ? (
                <span className="text-[10px] text-emerald-300/90 inline-flex items-center gap-1"
                      data-testid="maison-terrain-status">
                  <CheckCircle2 className="w-3 h-3" />
                  Terrain tracé sur {report.terrain_reference.floor_name}
                  {report.terrain_reference.area_m2 ? ` · ≈ ${report.terrain_reference.area_m2} m²` : ''}
                </span>
              ) : (
                <span className="text-[10px] text-amber-300 inline-flex items-center gap-1"
                      data-testid="maison-terrain-status">
                  <AlertTriangle className="w-3 h-3" />
                  Aucun terrain tracé sur le RDC (outil Terrain, 2a·Contours)
                </span>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="text-[9.5px] uppercase tracking-wider text-gray-500 block mb-0.5">Sols extérieurs</label>
                <input
                  type="text"
                  value={terrainSols}
                  onChange={(e) => setTerrainSols(e.target.value)}
                  placeholder="ex : dalles béton lavé, gazon…"
                  className="w-full h-7 px-2 rounded bg-black/30 border border-white/10 text-[11px] text-gray-200 focus:outline-none focus:border-emerald-400/50"
                  data-testid="maison-terrain-sols-input"
                />
              </div>
              <div>
                <label className="text-[9.5px] uppercase tracking-wider text-gray-500 block mb-0.5">Végétation</label>
                <input
                  type="text"
                  value={terrainVegetation}
                  onChange={(e) => setTerrainVegetation(e.target.value)}
                  placeholder="ex : haies basses, érable, massif fleuri…"
                  className="w-full h-7 px-2 rounded bg-black/30 border border-white/10 text-[11px] text-gray-200 focus:outline-none focus:border-emerald-400/50"
                  data-testid="maison-terrain-vegetation-input"
                />
              </div>
            </div>
            <div className="mt-2 flex items-start gap-1.5 text-[10px] text-teal-300/90 leading-snug"
                 data-testid="maison-chained-info">
              <Link2 className="w-3 h-3 shrink-0 mt-0.5" />
              <span>
                <strong>Batch chaîné</strong> : le RDC est généré en premier, puis son rendu
                (jardin, terrain, abords) sert de référence stricte aux autres étages —
                l'environnement visible par les fenêtres reste identique du sous-sol aux combles.
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="text-[10.5px] text-gray-400">
              {readyCount} étage(s) prêt(s)
              {warnCount > 0 && <span className="text-amber-400"> · {warnCount} avert.</span>}
              {errorCount > 0 && <span className="text-rose-400"> · {errorCount} bloqué(s)</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadReport} disabled={loading}
                      className="h-8 text-[11px] border-white/15 bg-transparent text-gray-300 hover:bg-white/5"
                      data-testid="maison-batch-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Re-vérifier
              </Button>
              <Button onClick={launch} disabled={launching || readyCount === 0} size="sm"
                      className="h-8 text-[11.5px] bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                      data-testid="maison-batch-launch-btn">
                {launching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5 mr-1.5" />}
                Lancer {readyCount} étage(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
